import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { app } from './firebase';

let messaging: Messaging | null = null;

// Initialize Firebase Cloud Messaging
export const initializeMessaging = () => {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      messaging = getMessaging(app);
      return messaging;
    } catch (error) {
      return null;
    }
  }
  return null;
};

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    if (!('Notification' in window)) {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    return false;
  }
};

/**
 * Get FCM token for push notifications
 * Requires VAPID key from Firebase Console
 */
export const getFCMToken = async (): Promise<string | null> => {
  try {
    const messagingInstance = messaging || initializeMessaging();
    if (!messagingInstance) {
      throw new Error('Messaging not initialized');
    }

    // Check if permission is granted
    if (Notification.permission !== 'granted') {
      const granted = await requestNotificationPermission();
      if (!granted) {
        return null;
      }
    }

    // Get registration token
    // You need to add your VAPID key from Firebase Console here
    const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

    if (!vapidKey) {
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey,
    });

    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Save FCM token to user profile in Firestore
 */
export const saveFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    await fetch('/api/notifications/save-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, token }),
    });
  } catch (error) {
  }
};

/**
 * Listen for foreground messages
 */
export const onMessageListener = (callback: (payload: any) => void) => {
  const messagingInstance = messaging || initializeMessaging();
  if (!messagingInstance) {
    return () => {};
  }

  return onMessage(messagingInstance, (payload) => {
    callback(payload);
  });
};

/**
 * Show browser notification
 */
export const showNotification = (title: string, options?: NotificationOptions) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
};
